import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightLeft, ArrowUpRight, FileCheck2, Loader2, Clock, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { transferirAnaliseTermo, adiarAnaliseTermo } from '../lib/termoAnaliseHandoff';
import { temDepartamento } from '../lib/departamentos';
import { Profile } from '../../types';
import TransferUserModal from '../../views/CRM/components/NegotiationDetails/TransferUserModal';

/** "Analista ou acima" — sem hierarquia de departamentos no sistema, o alvo da transferência é resolvido por role. */
const ROLES_ANALISE = ['analista', 'gerente', 'adm'];

/** O pop-up automático fica restrito a quem tem o departamento Pós Venda marcado, independente do role. */
const podeVerPopup = (p: Profile | null | undefined) => temDepartamento(p, 'Pós Venda');

interface PendingItem {
    id: number;
    titulo: string;
    cliente_nome: string;
    responsavel_id?: string;
    responsavelNome?: string;
    responsavelRole?: string;
    data_assinatura_termo?: string | null;
}

/** Mesma distinção de papel do badge em views/CRM/Kanban/KanbanCard.tsx (vendedor x parceiro/vendedor_parceiro). */
const badgePapel = (role?: string) => role === 'vendedor'
    ? { label: 'Vendedor', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400' }
    : (role === 'parceiro' || role === 'vendedor_parceiro')
        ? { label: 'Parceiro', className: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-400' }
        : null;

const fmtData = (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * Pop-up exibido ao departamento Pós Venda ao abrir o sistema, listando toda
 * negociação com status='termo_assinado' (termo assinado, aguardando início
 * da análise cadastral). Mesmo modelo do pop-up de aprovação de desconto
 * (src/components/AprovacaoDescontoPopup.tsx), mas sem aprovar/rejeitar —
 * aqui a decisão é sobre quem/quando faz a análise, não sobre o termo em si.
 */
const TermoAssinadoPopup = () => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();
    const navigate = useNavigate();

    const [pendencias, setPendencias] = useState<PendingItem[]>([]);
    const [loadingId, setLoadingId] = useState<number | null>(null);
    const [transferindo, setTransferindo] = useState<PendingItem | null>(null);
    const [adiando, setAdiando] = useState<PendingItem | null>(null);
    const [observacaoAdiar, setObservacaoAdiar] = useState('');

    const podeVer = podeVerPopup(profile);

    const fetchPendencias = useCallback(async () => {
        if (!user || !podeVer) return;

        const { data: negs } = await supabase
            .from('negociacoes')
            .select('id, titulo, cliente_nome, responsavel_id, data_assinatura_termo')
            .eq('status', 'termo_assinado');

        if (!negs || negs.length === 0) {
            setPendencias([]);
            return;
        }

        const ids = negs.map(n => n.id);
        const responsavelIds = Array.from(new Set(negs.map(n => n.responsavel_id).filter(Boolean))) as string[];

        const [{ data: notas }, perfisResult] = await Promise.all([
            supabase
                .from('negociacao_notas')
                .select('negociacao_id, conteudo, created_at, user_id')
                .in('negociacao_id', ids)
                .like('conteudo', '%[TERMO_ANALISE_ADIADA:%')
                .order('created_at', { ascending: false }),
            responsavelIds.length > 0
                ? supabase.from('profiles').select('id, full_name, role').in('id', responsavelIds)
                : Promise.resolve({ data: [] as { id: string; full_name: string; role: string }[] }),
        ]);

        // Adiamento só conta se foi o próprio usuário logado quem adiou (decisão: não é global).
        const adiadoAtePorNegociacao = new Map<number, Date>();
        (notas || []).forEach((n: { negociacao_id: number; conteudo: string; user_id: string }) => {
            if (n.user_id === user.id && !adiadoAtePorNegociacao.has(n.negociacao_id)) {
                const iso = n.conteudo.match(/^\[TERMO_ANALISE_ADIADA:([^\]]+)\]/)?.[1];
                if (iso) adiadoAtePorNegociacao.set(n.negociacao_id, new Date(iso));
            }
        });

        const nomePorId = new Map((perfisResult.data || []).map((p: { id: string; full_name: string }) => [p.id, p.full_name]));
        const rolePorId = new Map((perfisResult.data || []).map((p: { id: string; role: string }) => [p.id, p.role]));

        const visiveis = negs.filter(n => {
            const adiadoAte = adiadoAtePorNegociacao.get(n.id);
            return !adiadoAte || adiadoAte <= new Date();
        });

        setPendencias(visiveis.map(n => ({
            id: n.id,
            titulo: n.titulo,
            cliente_nome: n.cliente_nome,
            responsavel_id: n.responsavel_id,
            responsavelNome: n.responsavel_id ? nomePorId.get(n.responsavel_id) : undefined,
            responsavelRole: n.responsavel_id ? rolePorId.get(n.responsavel_id) : undefined,
            data_assinatura_termo: n.data_assinatura_termo,
        })));
    }, [user, podeVer]);

    useEffect(() => { fetchPendencias(); }, [fetchPendencias]);

    const fecharCard = (id: number) => setPendencias(prev => prev.filter(p => p.id !== id));

    const handleTransferirSelecionado = async (destino: Profile) => {
        if (!user || !transferindo) return;
        setLoadingId(transferindo.id);
        try {
            await transferirAnaliseTermo(transferindo, user.id, profile?.full_name, destino);
            addToast(`Análise transferida para ${destino.full_name}.`, 'success');
            setTransferindo(null);
        } catch (e: any) {
            addToast('Erro ao transferir: ' + (e.message || 'Erro desconhecido'), 'error');
        } finally {
            setLoadingId(null);
        }
    };

    const handleConfirmarAdiar = async (item: PendingItem) => {
        if (!user) return;
        if (!observacaoAdiar.trim()) {
            addToast('Informe uma observação para adiar.', 'error');
            return;
        }
        setLoadingId(item.id);
        try {
            await adiarAnaliseTermo(item, user.id, profile?.full_name, observacaoAdiar.trim());
            fecharCard(item.id);
            addToast('Análise adiada por 1 dia.', 'success');
            setAdiando(null);
            setObservacaoAdiar('');
        } catch (e: any) {
            addToast('Erro ao adiar: ' + (e.message || 'Erro desconhecido'), 'error');
        } finally {
            setLoadingId(null);
        }
    };

    const handleRealizarAnalise = (item: PendingItem) => {
        setPendencias([]);
        navigate(`/gestao?negociacaoId=${item.id}`);
    };

    if (!podeVer || pendencias.length === 0) return null;

    return (
        <>
            <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
                    {/* Header */}
                    <div className="flex items-center gap-3 px-6 py-5 border-b border-teal-200 dark:border-teal-800/40 bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 shrink-0">
                        <div className="p-2.5 bg-teal-100 dark:bg-teal-900/40 rounded-xl">
                            <FileCheck2 size={20} className="text-teal-600" />
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-teal-900 dark:text-teal-300">
                                {pendencias.length === 1 ? '1 termo assinado aguardando análise' : `${pendencias.length} termos assinados aguardando análise`}
                            </h3>
                            <p className="text-xs text-teal-600 dark:text-teal-400 font-medium">Inicie a validação de dados ou transfira para outro responsável.</p>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="overflow-y-auto custom-scrollbar p-6 space-y-4">
                        {pendencias.map(item => {
                            const busy = loadingId === item.id;
                            const papel = badgePapel(item.responsavelRole);
                            return (
                                <div key={item.id} className="rounded-xl border border-gray-200 dark:border-slate-700 p-4 space-y-3">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                                                {item.cliente_nome || item.titulo}
                                            </h4>
                                            <p className="text-xs text-gray-500 dark:text-slate-400">
                                                Responsável: {item.responsavelNome || 'vendedor'}
                                            </p>
                                        </div>
                                        {papel && (
                                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold shrink-0 ${papel.className}`}>
                                                {papel.label}
                                            </span>
                                        )}
                                    </div>

                                    <p className="text-sm text-gray-700 dark:text-slate-300">
                                        Termo assinado em {fmtData(item.data_assinatura_termo)}.
                                    </p>

                                    {adiando?.id === item.id ? (
                                        <div className="flex flex-col gap-2 pt-1">
                                            <textarea
                                                value={observacaoAdiar}
                                                onChange={e => setObservacaoAdiar(e.target.value)}
                                                placeholder="Motivo do adiamento (obrigatório)..."
                                                rows={2}
                                                className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-xs bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 resize-none"
                                                autoFocus
                                            />
                                            <div className="flex items-center gap-2 justify-end">
                                                <button
                                                    type="button"
                                                    onClick={() => { setAdiando(null); setObservacaoAdiar(''); }}
                                                    className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 text-xs font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                                                >
                                                    Cancelar
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleConfirmarAdiar(item)}
                                                    disabled={busy}
                                                    className="px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-bold hover:bg-primary/90 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                                >
                                                    {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Confirmar
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                    <div className="flex flex-wrap items-center gap-2 pt-1">
                                        <button
                                            type="button"
                                            onClick={() => { setAdiando(item); setObservacaoAdiar(''); }}
                                            disabled={busy}
                                            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 text-xs font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                        >
                                            <Clock size={13} /> Adiar
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setTransferindo(item)}
                                            disabled={busy}
                                            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 text-xs font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                        >
                                            <ArrowRightLeft size={13} /> Transferir
                                        </button>
                                        <div className="flex-1" />
                                        <button
                                            type="button"
                                            onClick={() => handleRealizarAnalise(item)}
                                            disabled={busy}
                                            className="px-3 py-1.5 rounded-lg bg-teal-600 text-white text-xs font-bold hover:bg-teal-700 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                        >
                                            <ArrowUpRight size={13} /> Realizar análise
                                        </button>
                                    </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <TransferUserModal
                isOpen={!!transferindo}
                onClose={() => setTransferindo(null)}
                onSelect={handleTransferirSelecionado}
                currentResponsibleId={user?.id}
                roleFilter={ROLES_ANALISE}
                title="Transferir Validação de Dados"
            />
        </>
    );
};

export default TermoAssinadoPopup;
